import React, { useState } from 'react';
import { 
  TextField, 
  InputAdornment, 
  IconButton, 
  Box 
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

const PasswordInput = ({
  value,
  onChange,
  label = 'Password',
  placeholder = 'Enter your password',
  error = false,
  helperText = '',
  showStrengthIndicator = true,
  showDetails = true,
  fullWidth = true,
  variant = 'outlined',
  size = 'medium',
  required = false,
  disabled = false,
  ...otherProps
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isAutofilled, setIsAutofilled] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  // Handler for autofill detection
  const handleAnimationStart = (e) => {
    if (e.animationName === 'mui-auto-fill') {
      setIsAutofilled(true);
    } else if (e.animationName === 'mui-auto-fill-cancel') {
      setIsAutofilled(false);
    }
  };

  return (
    <Box>
      <TextField
        type={showPassword ? 'text' : 'password'}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        variant={variant}
        size={size}
        required={required}
        disabled={disabled}
        InputLabelProps={{ shrink: Boolean(value?.length) || isAutofilled || isFocused }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleTogglePassword}
                edge="end"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
          inputProps: {
            onAnimationStart: handleAnimationStart,
            onFocus: () => setIsFocused(true),
            onBlur: () => setIsFocused(false),
            style: {},
          },
        }}
        {...otherProps}
      />
      
      {showStrengthIndicator && value && (
        <PasswordStrengthIndicator 
          password={value}
          showDetails={showDetails}
          showPassword={showPassword}
          onTogglePassword={handleTogglePassword}
          showToggleButton={false} // We already have the toggle in the input field
        />
      )}
      {/* Add autofill CSS animation for detection */}
      <style>{`
        input:-webkit-autofill {
          animation-name: mui-auto-fill;
          animation-duration: 0.01s;
          animation-iteration-count: 1;
        }
        input:not(:-webkit-autofill) {
          animation-name: mui-auto-fill-cancel;
          animation-duration: 0.01s;
          animation-iteration-count: 1;
        }
      `}</style>
    </Box>
  );
};

export default PasswordInput; 